import { CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PureDate } from '@/lib/absolute-datetime'
import { getMapsUrl } from '@/lib/timesheet-utils'
import type { Timesheet } from '@/types/timesheet'

interface TimesheetLinesReadOnlyProps {
	lines: Timesheet['lines']
	canManage?: boolean
	onEditLine?: (line: Timesheet['lines'][0]) => void
}

export function TimesheetLinesReadOnly({ lines, canManage, onEditLine }: TimesheetLinesReadOnlyProps) {
	return (
		<div className="rounded-md border overflow-x-auto">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="w-12">#</TableHead>
						<TableHead className="min-w-[80px]">Inicio</TableHead>
						<TableHead className="min-w-[80px]">Fin</TableHead>
						<TableHead className="min-w-[100px]">Duración</TableHead>
						<TableHead className="min-w-[120px]">Tipo de hora</TableHead>
						<TableHead className="min-w-[120px]">Proyecto</TableHead>
						<TableHead className="min-w-[80px]">Ubicación</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{lines.map((line, index) => {
						const startMapsUrl = getMapsUrl(line.startGeolocation)
						const endMapsUrl = getMapsUrl(line.endGeolocation)

						return (
							<TableRow
								key={line.id}
								className="align-top cursor-pointer"
								onClick={() => {
									canManage && onEditLine && onEditLine(line)
								}}
							>
								<TableCell className="font-semibold flex items-center gap-1">
									{index + 1}{' '}
									<span className="text-xs text-muted-foreground">
										{line.approved ? <CheckCircle2 className="h-3 w-3 text-green-700" /> : ''}
									</span>
								</TableCell>
								<TableCell>
									<p className="text-sm font-medium">{new PureDate(line.start).getTime()}</p>
								</TableCell>
								<TableCell>
									<p className="text-sm font-medium">{new PureDate(line.end).getTime()}</p>
								</TableCell>
								<TableCell className="font-medium">
									{(() => {
										const diffMinutes = new PureDate(line.start).diffMinutes(new PureDate(line.end))
										const hours = Math.floor(diffMinutes / 60)
										const minutes = diffMinutes % 60
										if (hours === 0) return `${minutes}m`
										if (minutes === 0) return `${hours}h`
										return `${hours}h ${minutes}m`
									})()}
								</TableCell>
								<TableCell>
									<div className="space-y-1">
										<p className="text-sm font-medium">{line.hourType.name}</p>
									</div>
								</TableCell>
								<TableCell>
									<div className="space-y-1">
										<p className="text-sm font-medium">{line.project.name}</p>
									</div>
								</TableCell>
								<TableCell onClick={(e) => e.stopPropagation()}>
									{line.startGeolocation || line.endGeolocation ? (
										<div className="space-y-1">
											{startMapsUrl && (
												<div className="flex items-center gap-1">
													<span className="text-xs text-muted-foreground">Entrada:</span>
													<Button
														variant="link"
														size="sm"
														className="h-auto p-0 font-normal text-xs"
														onClick={() => {
															window.open(startMapsUrl, '_blank')
														}}
													>
														Ver mapa →
													</Button>
												</div>
											)}
											{endMapsUrl && (
												<div className="flex items-center gap-1">
													<span className="text-xs text-muted-foreground">Salida:</span>
													<Button
														variant="link"
														size="sm"
														className="h-auto p-0 font-normal text-xs"
														onClick={() => {
															window.open(endMapsUrl, '_blank')
														}}
													>
														Ver mapa →
													</Button>
												</div>
											)}
										</div>
									) : (
										<p className="text-sm text-muted-foreground">Sin datos</p>
									)}
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
		</div>
	)
}
